import { createRoute, lazyRouteComponent, useParams, type AnyRoute } from "@tanstack/react-router";
import { lazy } from "react";

/**
 * Routes of the investigation screens (specs explorer-home-search, explorer-user-list,
 * explorer-user-profile, explorer-user-transactions-map): home and search, transactions by IP, the
 * user and device list and the user profile. Screens load lazily; `parent` is the signed-in app
 * route (no environment parameter: every screen serves the installation's environment).
 */
export function createInvestigationRoutes<TParent extends AnyRoute>(parent: TParent) {
  const home = createRoute({
    getParentRoute: () => parent,
    path: "/",
    component: lazyRouteComponent(
      () => import("../features/investigation/home/home-page.tsx"),
      "HomePage",
    ),
  });
  const users = createRoute({
    getParentRoute: () => parent,
    path: "/users",
    component: lazyRouteComponent(
      () => import("../features/investigation/list/users-page.tsx"),
      "UsersPage",
    ),
  });
  const userProfile = createRoute({
    getParentRoute: () => parent,
    path: "/users/$ref",
    component: function UserProfileRoute() {
      const params: Record<string, unknown> = useParams({ strict: false });
      return typeof params.ref === "string" ? <UserProfileLazy playerRef={params.ref} /> : null;
    },
  });
  const ipTransactions = createRoute({
    getParentRoute: () => parent,
    path: "/ips/$ip",
    component: function IpTransactionsRoute() {
      const params: Record<string, unknown> = useParams({ strict: false });
      return typeof params.ip === "string" ? <IpTransactionsLazy ip={params.ip} /> : null;
    },
  });
  return [home, users, userProfile, ipTransactions] as const;
}

const UserProfileLazy = lazy(() =>
  import("../features/investigation/profile/user-profile-page.tsx").then((m) => ({
    default: m.UserProfilePage,
  })),
);
const IpTransactionsLazy = lazy(() =>
  import("../features/investigation/home/ip-transactions-page.tsx").then((m) => ({
    default: m.IpTransactionsPage,
  })),
);
